import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { ChlorineSystemConfig } from "@/types"; // Assume TRPC types inferred, use any for now

interface ChlorineConfigTableProps {
  configs: any[]; // Replace with proper type from TRPC
  onEdit: (config: any) => void;
  onDelete: (configId: number) => void;
  isEditable: boolean;
}

export function ChlorineConfigTable({ configs, onEdit, onDelete, isEditable }: ChlorineConfigTableProps) {
  // Sort by activeFrom DESC (newest first)
  const sortedConfigs = [...configs].sort((a, b) => new Date(b.activeFrom).getTime() - new Date(a.activeFrom).getTime());

  // Display-only formatter guard.
  // TRPC/drizzle DECIMAL fields can arrive as string; never call .toFixed on unknown types.
  const fmtNum = (v: unknown, digits: number) => {
    const n = typeof v === "number" ? v : (v === null || v === undefined || v === "" ? NaN : Number(v));
    return Number.isFinite(n) ? n.toFixed(digits) : "—";
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString();
  };

  const getStatusBadge = (config: any) => {
    if (!config.activeTo) {
      return <Badge variant="default" className="bg-green-500 hover:bg-green-500">Active</Badge>;
    }
    return <Badge variant="secondary">Historical</Badge>;
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Active From</TableHead>
            <TableHead>Active To</TableHead>
            <TableHead>Gallons per Inch</TableHead>
            <TableHead>Lbs Cl₂ per Gallon</TableHead>
            <TableHead>Notes</TableHead>
            {isEditable && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedConfigs.map((config) => (
            <TableRow key={config.id}>
              <TableCell>{getStatusBadge(config)}</TableCell>
              <TableCell>{formatDate(config.activeFrom)}</TableCell>
              <TableCell>{config.activeTo ? formatDate(config.activeTo) : "Present"}</TableCell>
              <TableCell className={config.activeTo ? "font-normal" : "font-semibold"}>{config.gallonsPerInch}</TableCell>
              <TableCell className={config.activeTo ? "font-normal" : "font-semibold"}>
                {fmtNum(config.lbsCl2PerGallon, 4)}
              </TableCell>
              <TableCell className="max-w-xs truncate" title={config.configNotes}>
                {config.configNotes || "—"}
              </TableCell>
              {isEditable && (
                <TableCell className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(config)}
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(config.id)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
          {sortedConfigs.length === 0 && (
            <TableRow>
              <TableCell colSpan={isEditable ? 7 : 6} className="h-24 text-center text-muted-foreground">
                No chlorine configurations found. {isEditable ? "Create the first one!" : "Contact administrator."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
